import { axios } from '../lib/axios';
import { BAY_API_URL } from '../lib/environment.js';
import Cookies from 'js-cookie';

const BASE_URL = `${BAY_API_URL}/listing-banners`;

const getMutationConfig = () => {
	const resolvedBayToken = Cookies.get('bayToken');
	if (!resolvedBayToken) {
		throw new Error('Missing bayToken for listing banner mutation.');
	}

	return {
		headers: {
			'X-Session-Token': resolvedBayToken,
		},
	};
};

const ensureNonEmptyString = (value, fieldName) => {
	const normalized = typeof value === 'string' ? value.trim() : '';
	if (!normalized) {
		throw new Error(`${fieldName} is required.`);
	}
	return normalized;
};

const listingBanners = () => ({
	read: ({ accountId } = {}) => {
		const params = {};
		if (accountId) params.account_id = accountId;
		return axios
			.get(BASE_URL, { params })
			.then((response) => response)
			.catch((error) => {
				if (!error?.response && !error?.message) {
					throw new Error('Failed to load listing banners.');
				}

				throw error;
			});
	},

	create: ({ bannerText } = {}) => {
		const normalizedText = ensureNonEmptyString(bannerText, 'bannerText');

		return axios
			.post(
				BASE_URL,
				{
					banner_text: normalizedText,
				},
				getMutationConfig(),
			)
			.then((response) => response)
			.catch((error) => {
				if (!error?.response && !error?.message) {
					throw new Error('Failed to create listing banner.');
				}

				throw error;
			});
	},

	rename: ({
		bannerText,
		bannerId = null,
		bannerName = null,
	} = {}) => {
		const normalizedText = ensureNonEmptyString(bannerText, 'bannerText');
		const payload = {
			banner_text: normalizedText,
		};
		const mutationConfig = getMutationConfig();

		if (bannerId != null) {
			return axios
				.put(BASE_URL, payload, {
					...mutationConfig,
					params: { id: bannerId },
				})
				.then((response) => response)
				.catch((error) => {
					if (!error?.response && !error?.message) {
						throw new Error('Failed to rename listing banner.');
					}

					throw error;
				});
		}

		if (bannerName) {
			payload.name = bannerName;
			return axios
				.put(BASE_URL, payload, mutationConfig)
				.then((response) => response)
				.catch((error) => {
					if (!error?.response && !error?.message) {
						throw new Error('Failed to rename listing banner.');
					}

					throw error;
				});
		}

		throw new Error('bannerId or bannerName is required for rename.');
	},

	reorder: ({ orderedIndexes } = {}) => {
		if (!Array.isArray(orderedIndexes) || orderedIndexes.length === 0) {
			throw new Error('OrderedIndexes must be a non-empty array.');
		}

		return axios
			.put(
				BASE_URL,
				{
					ordered_indexes: orderedIndexes,
				},
				getMutationConfig(),
			)
			.then((response) => response)
			.catch((error) => {
				if (!error?.response && !error?.message) {
					throw new Error('Failed to reorder listing banners.');
				}

				throw error;
			});
	},

	remove: ({
		replacementName,
		bannerId = null,
		bannerName = null,
	} = {}) => {
		const normalizedReplacementName = ensureNonEmptyString(
			replacementName,
			'replacementName',
		);
		const payload = {
			replacement_name: normalizedReplacementName,
		};
		const config = getMutationConfig();

		if (bannerId != null) {
			config.params = { id: bannerId };
		} else if (bannerName) {
			payload.name = bannerName;
		} else {
			throw new Error('bannerId or bannerName is required for delete.');
		}

		return axios
			.delete(BASE_URL, { ...config, data: payload })
			.then((response) => response)
			.catch((error) => {
				if (!error?.response && !error?.message) {
					throw new Error('Failed to delete listing banner.');
				}

				throw error;
			});
	},
});

const singleton = listingBanners();
Object.freeze(singleton);

export default singleton;
